import React from 'react';
import { ClipRenderState, isCancelledRender } from './renderState';
import { friendlyRenderError } from './persistenceLabels';

interface RenderProgressBarProps {
  state: ClipRenderState;
}

/** Human label for the backend render stage. */
const stageLabel = (stage: string): string => {
  switch (stage) {
    case 'encoding':
    case 'muxing':
      return 'Encoding';
    case 'stills':
      return 'Rendering slides';
    case 'planning':
      return 'Planning';
    case 'sealing':
      return 'Finishing video';
    case 'uploading':
      return 'Saving';
    default:
      return 'Rendering';
  }
};

/**
 * Compact render feedback shown in the render actions area: a determinate
 * progress bar while rendering, a friendly error strip on failure. Renders
 * nothing for idle/ready states (the completion toast covers success).
 */
const RenderProgressBar: React.FC<RenderProgressBarProps> = ({ state }) => {
  // A deliberate cancel is not a crash: neutral styling, no alarm, no "Render
  // failed —" prefix. Checked before the generic failed branch below.
  if (isCancelledRender(state)) {
    return (
      <div className="border border-white/20 bg-white/5 px-3 py-2 text-xs font-mono text-zinc-300">
        Render cancelled
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div
        role="alert"
        className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-mono text-red-200"
      >
        Render failed — {friendlyRenderError(state.error)}
      </div>
    );
  }

  if (state.status !== 'rendering') return null;

  const percent = Math.round(state.progress * 100);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400">
        <span>{stageLabel(state.stage)}</span>
        <span>{percent}%</span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden bg-white/10"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div
          className="h-full bg-white transition-[width] duration-300 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

export default RenderProgressBar;
